import { FIXED_DT, MAX_FRAME_DELTA } from './tuning';
import type { TimeControl } from './Time';

export interface LoopHandlers {
  /** 고정 간격 물리 스텝. 항상 FIXED_DT로 호출된다. */
  fixedStep(dt: number): void;
  /**
   * 렌더. alpha는 마지막 물리 스텝과 다음 스텝 사이의 보간 계수(0~1).
   * 120Hz 화면에서 60Hz 물리가 끊겨 보이지 않도록 이 값으로 보간한다.
   */
  render(alpha: number): void;
}

/**
 * 고정 timestep accumulator 루프.
 *
 * 물리를 렌더 프레임레이트에 묶으면 기기마다 다른 결과가 나오고,
 * Rapier의 결정론이 무의미해져 스냅샷 테스트가 불가능해진다.
 * 그래서 물리는 항상 FIXED_DT로만 전진하고, 남는 시간은 보간으로 처리한다.
 */
export class Loop {
  private accumulator = 0;
  private lastTime: number | null = null;
  private frameHandle = 0;
  private running = false;

  private stepsThisFrame = 0;
  private smoothedFps = 0;

  constructor(
    private readonly handlers: LoopHandlers,
    private readonly time: TimeControl,
  ) {}

  /** 직전 프레임에 실행된 물리 스텝 수. 디버그 표시용. */
  get lastStepCount(): number {
    return this.stepsThisFrame;
  }

  /** 지수 평활된 프레임레이트. 디버그 표시용. */
  get fps(): number {
    return this.smoothedFps;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = null;
    this.frameHandle = requestAnimationFrame(this.tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.frameHandle);
  }

  private readonly tick = (nowMs: number): void => {
    if (!this.running) return;
    this.frameHandle = requestAnimationFrame(this.tick);

    const now = nowMs / 1000;

    // 첫 프레임은 기준 시각만 잡고 넘어간다. 그러지 않으면 delta가 페이지 로드 시각이 된다.
    if (this.lastTime === null) {
      this.lastTime = now;
      return;
    }

    const realDelta = Math.min(now - this.lastTime, MAX_FRAME_DELTA);
    this.lastTime = now;

    if (realDelta > 0) {
      this.smoothedFps = this.smoothedFps === 0 ? 1 / realDelta : this.smoothedFps * 0.9 + (1 / realDelta) * 0.1;
    }

    this.accumulator += this.time.advance(realDelta);

    this.stepsThisFrame = 0;
    while (this.accumulator >= FIXED_DT) {
      this.handlers.fixedStep(FIXED_DT);
      this.accumulator -= FIXED_DT;
      this.stepsThisFrame += 1;
    }

    this.handlers.render(this.accumulator / FIXED_DT);
  };
}
